import { useCallback, useEffect, useState } from 'react';

// Light or dark, chosen in this order:
//   1. what the visitor last picked with the header toggle (remembered here)
//   2. otherwise whatever their phone or laptop is set to
//
// The same key and attribute are read by the small script in public/index.html,
// which stamps the theme before first paint so a dark-mode visitor never sees a
// white flash.
export const THEME_STORAGE_KEY = 'gsf-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';
const THEMES = ['light', 'dark'];

function readChoice() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(saved) ? saved : null;
  } catch (error) {
    // Private browsing can refuse storage entirely; fall back to the device.
    return null;
  }
}

function saveChoice(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    // Nothing to do - the choice just will not survive a reload.
  }
}

export default function useTheme() {
  const [choice, setChoice] = useState(readChoice);
  const [osPrefersDark, setOsPrefersDark] = useState(
    () => window.matchMedia?.(DARK_QUERY).matches ?? false
  );

  useEffect(() => {
    const mq = window.matchMedia?.(DARK_QUERY);
    if (!mq) return undefined;

    const onChange = event => setOsPrefersDark(event.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari before 14 only has the deprecated listener API.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const theme = choice ?? (osPrefersDark ? 'dark' : 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setChoice(next);
    saveChoice(next);
  }, [theme]);

  return { theme, choice, toggle };
}
